class QuickAddModal {
  constructor() {
    this.modal = document.getElementById("quick-add-modal");
    this.modalContent = document.getElementById("quick-add-modal-content");
    this.closeButton = document.querySelector(".quick-add-modal__close");
    this.currentProductHandle = null;
    this.currentProductData = null;
    this.currentZoomIndex = 0; // Track current zoomed image index

    this.initEvents();
  }

  initEvents() {
    this.closeButton.addEventListener("click", () => this.close());
    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal) this.close();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.close();
    });

    // Handle quick-add buttons - both direct add and modal
    document.addEventListener("click", (e) => {
      const quickAddBtn = e.target.closest(".quick-add-btn");
      if (!quickAddBtn) return;

      e.preventDefault();
      const productHandle = quickAddBtn.dataset.productHandle;
      const hasVariants = quickAddBtn.dataset.hasVariants === "true";
      const variantId = quickAddBtn.dataset.variantId;

      if (!productHandle) return;

      // Direct add to cart for single variant products
      if (!hasVariants && variantId) {
        this.addToCartDirect(variantId, quickAddBtn);
      } else {
        // Open modal for products with variants
        this.open(productHandle);
      }
    });
  }

  open(productHandle) {
    if (
      this.currentProductHandle === productHandle &&
      this.modal.style.display === "flex"
    ) {
      return;
    }

    this.currentProductHandle = productHandle;
    this.showLoading();
    this.modal.style.display = "flex";
    document.body.style.overflow = "hidden";

    this.fetchProduct(productHandle);
  }

  close() {
    this.modal.style.display = "none";
    document.body.style.overflow = "";
    this.currentProductHandle = null;
    this.currentProductData = null;
  }

  async addToCartDirect(variantId, button) {
    const originalText =
      button.querySelector(".quick-add-btn-label")?.textContent ||
      button.textContent;
    const labelElement = button.querySelector(".quick-add-btn-label");

    // Disable button and show loading state
    button.disabled = true;
    if (labelElement) {
      labelElement.textContent = "Adding...";
    } else {
      button.textContent = "Adding...";
    }

    try {
      const cartUrl = window.routes.cart_add_url + ".js";
      const response = await fetch(cartUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Publish cart update event
        publish(PUB_SUB_EVENTS.cartUpdate, {
          action: "add",
          variant: data,
          productId: button.dataset.productId,
        });

        // Show success state
        if (labelElement) {
          labelElement.textContent = "Added!";
        } else {
          button.textContent = "Added!";
        }

        // Reset after delay
        setTimeout(() => {
          button.disabled = false;
          if (labelElement) {
            labelElement.textContent = originalText;
          } else {
            button.textContent = originalText;
          }
        }, 1500);
      } else {
        throw new Error(data.description || "Failed to add to cart");
      }
    } catch (error) {
      console.error("Error adding to cart:", error);

      // Publish error event
      publish(PUB_SUB_EVENTS.cartError, {
        error: error.message,
        context: "quick-add-direct",
        productId: button.dataset.productId,
      });

      if (labelElement) {
        labelElement.textContent = "Error - Try Again";
      } else {
        button.textContent = "Error - Try Again";
      }

      setTimeout(() => {
        button.disabled = false;
        if (labelElement) {
          labelElement.textContent = originalText;
        } else {
          button.textContent = originalText;
        }
      }, 2000);
    }
  }

  showLoading() {
    this.modalContent.innerHTML = `
      <div class="quick-add-loading">
        <p class="loading-text">Loading product</p>
        <div class="loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
    `;
  }

  showError() {
    this.modalContent.innerHTML = `
      <div class="quick-add-error">
        <p>Failed to load product. Please try again.</p>
        <button onclick="quickAddModal.close()">Close</button>
      </div>
    `;
  }

  async fetchProduct(productHandle) {
    try {
      const response = await fetch(
        `/products/${productHandle}?section_id=zeus-product-main`
      );
      if (!response.ok) throw new Error("Failed to fetch product");

      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");
      const sec =
        doc.querySelector(".shopify-section") ||
        doc.querySelector("[id^='shopify-section-']");
      const zeusRoot = doc.querySelector(".zeus-product-main");
      if (!zeusRoot) throw new Error("Invalid product content");

      sec
        ? sec.querySelectorAll('link[rel="stylesheet"]').forEach((l) => {
            if (
              l.href &&
              !Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some(
                (x) => x.href === l.href
              )
            ) {
              document.head.appendChild(l.cloneNode(true));
            }
          })
        : null;
      const linkFallback = doc.querySelector(
        'link[rel="stylesheet"][href*="zeus-product-main"]'
      );
      if (
        linkFallback &&
        !document.querySelector('link[href*="zeus-product-main.css"]')
      ) {
        document.head.appendChild(linkFallback.cloneNode(true));
      }

      const wrapper = document.createElement("div");
      wrapper.className = "quick-add-product zeus-quick-add-product";
      wrapper.dataset.productHandle = productHandle;
      if (sec) {
        wrapper.appendChild(document.importNode(sec, true));
      } else {
        wrapper.appendChild(document.importNode(zeusRoot, true));
      }

      this.modalContent.innerHTML = "";
      this.modalContent.appendChild(wrapper);

      const jsonEl = this.modalContent.querySelector("[data-zeus-product-json]");
      if (jsonEl && jsonEl.textContent) {
        try {
          const p = JSON.parse(jsonEl.textContent);
          this.currentProductData = p && p.variants ? p.variants : null;
        } catch (_) {
          this.currentProductData = null;
        }
      } else {
        this.currentProductData = null;
      }

      this.initZeusProductInModal();
    } catch (error) {
      console.error("Quick add error:", error);
      this.showError();
    }
  }

  initZeusProductInModal() {
    const wrap = this.modalContent.querySelector(".quick-add-product");
    const root = wrap && wrap.querySelector(".zeus-product-main");
    if (!root) return;
    if (typeof window.initZeusProductMainNode !== "function") {
      console.warn("Zeus product script not loaded");
      return;
    }
    delete root.dataset.zeusProductBound;
    window.initZeusProductMainNode(root);
  }
}

// Initialize everything when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  // Initialize global instances
  window.quickAddModal = new QuickAddModal();

  // Event delegation for color swatches
  document.addEventListener("click", function (e) {
    // Color swatches
    const colorOption = e.target.closest(
      ".card-product__color-option:not(.disabled)"
    );
    if (colorOption) {
      e.preventDefault();
      if (colorOption.getAttribute("aria-current") === "true") return;

      const productCard = colorOption.closest(".card-product");
      const desktopImage = productCard.querySelector(
        ".card-product__image-link.hidden-mobile .card-product__image"
      );
      const mobileImage = productCard.querySelector(
        ".card-product__image-container.visible-mobile .card-product__image"
      );

      // Update active state
      colorOption
        .closest(".card-product__colors")
        .querySelectorAll(".card-product__color-option")
        .forEach((btn) => btn.setAttribute("aria-current", "false"));
      colorOption.setAttribute("aria-current", "true");

      // Update images
      const newImageSrc = colorOption.dataset.imageSrc;
      const newMobileImageSrc = colorOption.dataset.imageSrcMobile;
      const newAltText = colorOption.dataset.imageAlt;

      if (desktopImage && newImageSrc) desktopImage.src = newImageSrc;
      if (mobileImage && newMobileImageSrc) mobileImage.src = newMobileImageSrc;
      if (desktopImage) desktopImage.alt = newAltText;
      if (mobileImage) mobileImage.alt = newAltText;
    }
  });
});
